import { LegalLayout, LegalSection } from "../components/Legal";

const SECTIONS = [
  {
    title: "Our Commitment to Your Privacy",
    content: (
      <p>
        Cindyrella Cleaning LLC (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your
        privacy and is committed to protecting the personal information you share with us. This
        Privacy Policy explains what information we collect, how we use and protect it, and the
        rights you have regarding your information. It applies to our website
        (cindyrellacleaningllc.com), our online estimate tools, our virtual chat assistant, and
        any communication you have with our team.
      </p>
    ),
  },
  {
    title: "Information We Collect",
    content: (
      <>
        <p>We collect only the information needed to provide our services:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Contact information:</strong> your name, phone number, and email address when
            you request a quote, use our estimate builder, fill out a form, or chat with our
            virtual assistant.
          </li>
          <li>
            <strong>Service information:</strong> details about your property, the type of service
            you are interested in, scheduling preferences, and any special instructions you provide.
          </li>
          <li>
            <strong>Website usage data:</strong> pages visited, time on site, device type, and
            general location, collected through analytics tools in aggregated form.
          </li>
        </ul>
        <p>
          We do not knowingly collect payment card data through this website, and we ask that you
          never share payment details, passwords, or other sensitive information through the chat
          assistant or web forms.
        </p>
      </>
    ),
  },
  {
    title: "How We Use Your Information",
    content: (
      <p>
        We use your information exclusively to: respond to your inquiry and prepare your
        personalized quote or estimate; schedule, provide, and follow up on cleaning services;
        communicate with you about appointments, changes, or service matters; improve our
        services and website experience; and comply with legal, tax, insurance, or contractual
        obligations.
      </p>
    ),
  },
  {
    title: "How We Share Information",
    content: (
      <>
        <p>
          We do not sell, rent, or trade your personal information to third parties for marketing
          purposes — ever. We share information only in these limited cases:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Service providers</strong> that help us operate (such as website hosting,
            scheduling, or analytics providers), under obligations to protect your data.
          </li>
          <li>
            <strong>Legal requirements:</strong> when disclosure is required by law, regulation,
            court order, or a valid government request.
          </li>
          <li>
            <strong>Business protection:</strong> to protect the rights, safety, and property of
            our clients, our team, or our company.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Cookies & Website Analytics",
    content: (
      <p>
        Our website uses cookies and Google Analytics to understand how visitors use the site
        (pages visited, time on site, general location). This data is aggregated and does not
        personally identify you. You can disable cookies in your browser settings and opt out of
        Google Analytics using tools such as the Google Analytics Opt-out Browser Add-on. The
        website remains fully usable with cookies disabled.
      </p>
    ),
  },
  {
    title: "Chatbot Conversations",
    content: (
      <p>
        Conversations with our virtual assistant may be stored to help us respond to your request
        and improve our service. These conversations are treated with the same care as any other
        personal information under this policy. Please do not share sensitive information (such
        as payment details) in the chat.
      </p>
    ),
  },
  {
    title: "Data Security",
    content: (
      <p>
        We take reasonable administrative, technical, and physical measures to protect your
        information against unauthorized access, loss, misuse, or alteration. Access to personal
        information is limited to team members who need it to serve you. While no method of
        transmission or storage is 100% secure, we work to protect your information using
        industry-standard practices.
      </p>
    ),
  },
  {
    title: "Data Retention",
    content: (
      <p>
        We keep your personal information only as long as necessary to provide our services,
        maintain business records required by law (such as tax and insurance records), and
        resolve any service matters. When information is no longer needed, we delete or
        anonymize it.
      </p>
    ),
  },
  {
    title: "Your Rights",
    content: (
      <p>
        You may at any time request to: access a copy of the personal information we hold about
        you; correct inaccurate or outdated information; delete your personal information,
        subject to legal record-keeping requirements; and opt out of non-essential
        communications. To exercise any of these rights, contact us at info@cindyrellaclean.com
        or (239) 244-1934. We will respond within a reasonable timeframe, normally within 30
        days.
      </p>
    ),
  },
  {
    title: "Children's Privacy",
    content: (
      <p>
        Our website and services are directed to adults. We do not knowingly collect personal
        information from children under 13. If you believe a child has provided us information,
        contact us and we will delete it promptly.
      </p>
    ),
  },
  {
    title: "Third-Party Links",
    content: (
      <p>
        Our website may contain links to third-party sites (such as our social media pages or
        Google Reviews). We are not responsible for the privacy practices of those sites and
        encourage you to review their policies.
      </p>
    ),
  },
  {
    title: "Changes to This Policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
        at the top reflects the most recent version. Significant changes will be posted on this
        page.
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <LegalLayout badge="Legal" title="Privacy Policy" testid="privacy-page">
      {SECTIONS.map(({ title, content }) => (
        <LegalSection key={title} title={title}>
          {content}
        </LegalSection>
      ))}
    </LegalLayout>
  );
}
